import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const DraftingWorkspace: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeClause, setActiveClause] = useState<number | null>(null);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState('Auto-saved 2 minutes ago');

  const highlightClause = (clauseNumber: number) => {
    setActiveClause(clauseNumber);
    const element = document.getElementById(`clause-${clauseNumber}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleSaveDraft = () => {
    setAutoSaveStatus('Saving draft...');
    setTimeout(() => {
      setAutoSaveStatus('Draft saved successfully');
    }, 1000);
  };

  const handleSendToReview = () => {
    if (window.confirm('Are you sure you want to send this agreement to Business Review? This action will change the status and notify the reviewers.')) {
      alert('Agreement has been sent to Business Review. Sarah Chen will be notified.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-14">
      <section className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-4">
            <button onClick={() => navigate('/board')} className="text-gray-600 hover:text-gray-900 flex items-center">
              <i className="fa-solid fa-arrow-left mr-2"></i>
              Back to Board
            </button>
            <div className="h-6 w-px bg-gray-300"></div>
            <nav className="flex items-center space-x-2 text-sm text-gray-600">
              <a href="/board" className="hover:text-violet-600">Agreements</a>
              <i className="fa-solid fa-chevron-right text-xs"></i>
              <a href="#" className="hover:text-violet-600">PT DEF 2026</a>
              <i className="fa-solid fa-chevron-right text-xs"></i>
              <span className="text-gray-900 font-medium">PKS – Maintenance Services</span>
              <i className="fa-solid fa-chevron-right text-xs"></i>
              <span className="text-violet-600 font-medium">Drafting</span>
            </nav>
          </div>
          <div className="flex items-center space-x-2">
            <span className="px-3 py-1.5 text-xs font-medium bg-yellow-100 text-yellow-700 rounded-lg flex items-center">
              <i className="fa-solid fa-circle text-yellow-500 text-xs mr-2 animate-pulse"></i>
              DRAFTING
            </span>
            <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center">
              <i className="fa-solid fa-clock-rotate-left mr-2"></i>
              Version History
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 mb-1">PKS – PT DEF – Maintenance Services 2026</h1>
            <div className="flex items-center space-x-4 text-sm text-gray-600">
              <span className="flex items-center">
                <i className="fa-solid fa-building mr-2 text-violet-600"></i>
                PT DEF 2026
              </span>
              <span className="flex items-center">
                <i className="fa-solid fa-file-contract mr-2 text-violet-600"></i>
                PKS
              </span>
              <span className="flex items-center">
                <i className="fa-regular fa-user mr-2 text-violet-600"></i>
                Legal Owner: David Lee
              </span>
              <span className="flex items-center">
                <i className="fa-regular fa-calendar mr-2 text-violet-600"></i>
                Due: Dec 25, 2024
              </span>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button onClick={handleSaveDraft} className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center">
              <i className="fa-regular fa-floppy-disk mr-2"></i>
              Save Draft
            </button>
            <button onClick={() => setShowCompareModal(true)} className="px-5 py-2.5 text-sm font-medium text-purple-700 bg-purple-50 border border-purple-300 rounded-lg hover:bg-purple-100 flex items-center">
              <i className="fa-solid fa-wand-magic-sparkles mr-2"></i>
              Run AI Compare
            </button>
            <button onClick={handleSendToReview} className="px-5 py-2.5 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 flex items-center">
              <i className="fa-solid fa-paper-plane mr-2"></i>
              Send to Business Review
            </button>
          </div>
        </div>

        <div className="mt-3 flex items-center text-xs text-gray-500">
          <i className="fa-solid fa-circle-check text-green-500 mr-2"></i>
          <span>{autoSaveStatus}</span>
        </div>
      </section>

      <div className="flex" style={{ height: 'calc(100vh - 180px)' }}>
        <aside className="w-[480px] bg-white border-r border-gray-200 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Agreement Parameters */}
            <section className="border border-gray-200 rounded-lg p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-semibold text-gray-900 flex items-center">
                  <i className="fa-solid fa-sliders mr-2 text-violet-600"></i>
                  Agreement Parameters
                </h2>
                <button className="text-xs text-violet-600 hover:text-violet-700 font-medium">Edit All</button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Company Name</label>
                  <input type="text" defaultValue="PT DEF 2026" readOnly className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-300 rounded-lg text-gray-900" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Agreement Type</label>
                  <select className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-violet-500">
                    <option>PKS (Perjanjian Kerjasama)</option>
                    <option>PO (Purchase Order)</option>
                    <option>BAST (Berita Acara Serah Terima)</option>
                    <option>Invoice</option>
                    <option>MOU</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">Contract Value</label>
                    <div className="relative">
                      <span className="absolute left-3 top-2.5 text-sm text-gray-500">Rp</span>
                      <input type="text" defaultValue="2,500,000,000" className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-violet-500" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">Duration</label>
                    <select className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-violet-500">
                      <option>12 Months</option>
                      <option>24 Months</option>
                      <option>36 Months</option>
                      <option>Custom</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">Start Date</label>
                    <input type="date" defaultValue="2026-01-01" className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-violet-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">End Date</label>
                    <input type="date" defaultValue="2026-12-31" className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-violet-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Payment Terms</label>
                  <select className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-violet-500">
                    <option>Net 30</option>
                    <option>Net 45</option>
                    <option>Net 60</option>
                    <option>Monthly Installment</option>
                    <option>Quarterly</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">SLA Response Time</label>
                  <div className="flex items-center space-x-2">
                    <input type="number" defaultValue={24} className="w-20 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-violet-500" />
                    <span className="text-sm text-gray-600">hours</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Service Coverage</label>
                  <textarea rows={3} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-violet-500" placeholder="Describe service coverage areas..." defaultValue="Infrastructure maintenance, system monitoring, security updates, technical support"></textarea>
                </div>
              </div>
            </section>

            {/* Clause Controls */}
            <section className="border border-gray-200 rounded-lg p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-semibold text-gray-900 flex items-center">
                  <i className="fa-solid fa-list-check mr-2 text-violet-600"></i>
                  Clause Controls
                </h2>
                <button className="text-xs text-violet-600 hover:text-violet-700 font-medium">Select All</button>
              </div>
              <div className="space-y-2">
                {[
                  { num: 1, title: 'Definitions', desc: 'Standard terminology and definitions' },
                  { num: 2, title: 'Scope of Work', desc: 'Detailed service specifications', variant: 'Extended' },
                  { num: 3, title: 'Payment Terms', desc: 'Billing and payment schedule' },
                  { num: 4, title: 'Intellectual Property', desc: 'IP rights and ownership' },
                  { num: 5, title: 'Service Level Agreement', desc: 'Performance metrics and SLA', variant: 'Extended' },
                  { num: 6, title: 'Confidentiality', desc: 'NDA and data protection' },
                  { num: 7, title: 'Liability & Indemnity', desc: 'Limitation of liability', checked: false },
                  { num: 8, title: 'Termination', desc: 'Contract termination conditions' },
                  { num: 9, title: 'Dispute Resolution', desc: 'Arbitration and mediation' },
                  { num: 10, title: 'Governing Law', desc: 'Jurisdiction and applicable law' },
                ].map((clause) => (
                  <div
                    key={clause.num}
                    onClick={() => highlightClause(clause.num)}
                    className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-all ${activeClause === clause.num ? 'bg-violet-50 border-violet-300' : 'border-gray-200 hover:bg-gray-50'}`}
                  >
                    <div className="flex items-center space-x-3 flex-1">
                      <input type="checkbox" defaultChecked={clause.checked !== false} className="w-4 h-4 text-violet-600 border-gray-300 rounded focus:ring-violet-500" onClick={(e) => e.stopPropagation()} />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Clause {clause.num}: {clause.title}</p>
                        <p className="text-xs text-gray-500">{clause.desc}</p>
                      </div>
                    </div>
                    <select className="px-2 py-1 text-xs border border-gray-300 rounded bg-white" onClick={(e) => e.stopPropagation()} defaultValue={clause.variant || 'Standard'}>
                      <option>Standard</option>
                      <option>Extended</option>
                      <option>Custom</option>
                    </select>
                  </div>
                ))}
              </div>
              <button className="w-full mt-4 px-4 py-2 text-sm font-medium text-violet-600 bg-violet-50 border border-violet-200 rounded-lg hover:bg-violet-100 flex items-center justify-center">
                <i className="fa-solid fa-plus mr-2"></i>
                Add Custom Clause
              </button>
            </section>

            {/* AI Suggestions */}
            <section className="border border-purple-200 bg-purple-50 rounded-lg p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-semibold text-purple-900 flex items-center">
                  <i className="fa-solid fa-wand-magic-sparkles mr-2 text-purple-600"></i>
                  AI Suggestions
                </h2>
                <button className="text-xs text-purple-600 hover:text-purple-700 font-medium">Refresh</button>
              </div>
              <div className="space-y-3">
                <div className="bg-white border border-purple-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                      <i className="fa-solid fa-lightbulb text-purple-600 text-sm"></i>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 mb-1">SLA Response Time Mismatch</p>
                      <p className="text-xs text-gray-600 mb-3">Supporting document PO_123.pdf specifies 12-hour response time, but current draft shows 24 hours. Consider aligning with PO terms.</p>
                      <div className="flex items-center space-x-2">
                        <button className="px-3 py-1.5 text-xs font-medium text-white bg-purple-600 rounded hover:bg-purple-700">Apply</button>
                        <button className="px-3 py-1.5 text-xs font-medium text-purple-600 bg-white border border-purple-300 rounded hover:bg-purple-50">View Source</button>
                        <button className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900">Ignore</button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-white border border-green-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <i className="fa-solid fa-circle-check text-green-600 text-sm"></i>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 mb-1">Compliance Check Passed</p>
                      <p className="text-xs text-gray-600">All mandatory clauses for PKS agreements are present and properly formatted according to company standards.</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-4 p-3 bg-purple-100 border border-purple-300 rounded-lg">
                <div className="flex items-center space-x-2 text-xs text-purple-900">
                  <i className="fa-solid fa-info-circle"></i>
                  <span>AI analysis based on 3 supporting documents and 47 historical agreements</span>
                </div>
              </div>
            </section>

            {/* Supporting Documents */}
            <section className="border border-gray-200 rounded-lg p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-semibold text-gray-900 flex items-center">
                  <i className="fa-solid fa-paperclip mr-2 text-violet-600"></i>
                  Supporting Documents
                </h2>
                <button className="text-xs text-violet-600 hover:text-violet-700 font-medium">Upload More</button>
              </div>
              <div className="space-y-2">
                {[
                  { name: 'PO_123_2025.pdf', size: '2.4 MB', type: 'pdf', used: true },
                  { name: 'Previous_Agreement_2024.docx', size: '1.8 MB', type: 'word', used: true },
                  { name: 'Service_Scope_Details.xlsx', size: '856 KB', type: 'excel', used: true },
                  { name: 'Business_Requirements.txt', size: '24 KB', type: 'text', used: false },
                ].map((doc) => (
                  <div key={doc.name} className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 cursor-pointer">
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 rounded flex items-center justify-center ${
                        doc.type === 'pdf' ? 'bg-red-100' :
                        doc.type === 'word' ? 'bg-violet-100' :
                        doc.type === 'excel' ? 'bg-green-100' : 'bg-gray-100'
                      }`}>
                        <i className={`fa-solid ${
                          doc.type === 'pdf' ? 'fa-file-pdf text-red-600' :
                          doc.type === 'word' ? 'fa-file-word text-violet-600' :
                          doc.type === 'excel' ? 'fa-file-excel text-green-600' : 'fa-file-lines text-gray-600'
                        }`}></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{doc.name}</p>
                        <p className="text-xs text-gray-500">{doc.size} {doc.used && '· Used by AI'}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button className="p-2 text-gray-600 hover:text-violet-600">
                        <i className="fa-solid fa-eye text-sm"></i>
                      </button>
                      <button className="p-2 text-gray-600 hover:text-violet-600">
                        <i className="fa-solid fa-download text-sm"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Collaboration */}
            <section className="border border-gray-200 rounded-lg p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-semibold text-gray-900 flex items-center">
                  <i className="fa-solid fa-users mr-2 text-violet-600"></i>
                  Collaboration
                </h2>
              </div>
              <div className="space-y-3">
                {[
                  { name: 'David Lee', role: 'Legal Owner', status: 'Editing now', online: true },
                  { name: 'Sarah Chen', role: 'Requestor', status: 'Can view', online: false },
                  { name: 'Michael Brown', role: 'Legal Reviewer', status: 'Can comment', online: false },
                ].map((person) => (
                  <div key={person.name} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <img src={`https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-${person.name === 'David Lee' ? '8' : person.name === 'Sarah Chen' ? '3' : '2'}.jpg`} className="w-8 h-8 rounded-full" alt={person.name} />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{person.name}</p>
                        <p className="text-xs text-gray-500">{person.role} · {person.status}</p>
                      </div>
                    </div>
                    {person.online && <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>}
                    {!person.online && <button className="text-xs text-violet-600 hover:text-violet-700">Manage</button>}
                  </div>
                ))}
              </div>
              <button className="w-full mt-4 px-4 py-2 text-sm font-medium text-violet-600 bg-violet-50 border border-violet-200 rounded-lg hover:bg-violet-100 flex items-center justify-center">
                <i className="fa-solid fa-user-plus mr-2"></i>
                Invite Collaborator
              </button>
            </section>
          </div>
        </aside>

        {/* Document Preview */}
        <main className="flex-1 bg-gray-100 overflow-y-auto">
          <div className="max-w-4xl mx-auto p-8">
            <div className="bg-white shadow-lg rounded-lg p-12 mb-6">
              <div className="text-center mb-8 pb-6 border-b-2 border-gray-300">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">PERJANJIAN KERJASAMA</h1>
                <h2 className="text-xl font-semibold text-gray-800 mb-4">MAINTENANCE SERVICES</h2>
                <p className="text-gray-600">No: PKS/DEF/2026/001</p>
              </div>

              {/* Document Content - Full clauses as in HTML */}
              <div className="mb-8">
                <p className="text-sm text-gray-700 leading-relaxed mb-4">
                  Perjanjian Kerjasama ini dibuat dan ditandatangani pada hari ini, tanggal 1 Januari 2026, oleh dan antara:
                </p>
                <div className="ml-6 mb-4">
                  <p className="text-sm font-semibold text-gray-900 mb-2">PIHAK PERTAMA:</p>
                  <p className="text-sm text-gray-700">PT DEF 2026</p>
                  <p className="text-sm text-gray-600">Berkedudukan di Jakarta, Indonesia</p>
                </div>
                <div className="ml-6 mb-4">
                  <p className="text-sm font-semibold text-gray-900 mb-2">PIHAK KEDUA:</p>
                  <p className="text-sm text-gray-700">PT Service Provider Indonesia</p>
                  <p className="text-sm text-gray-600">Berkedudukan di Jakarta, Indonesia</p>
                </div>
              </div>

              {/* All clauses from HTML */}
              {[1, 2, 3, 4, 5, 6, 8, 9, 10].map((num) => (
                <div
                  key={num}
                  id={`clause-${num}`}
                  className={`mb-8 p-4 rounded-lg transition-all ${activeClause === num ? 'bg-violet-50 border-l-4 border-violet-500' : ''}`}
                >
                  <h3 className="text-lg font-bold text-gray-900 mb-3">
                    {num === 1 && 'PASAL 1: DEFINISI'}
                    {num === 2 && 'PASAL 2: RUANG LINGKUP PEKERJAAN'}
                    {num === 3 && 'PASAL 3: KETENTUAN PEMBAYARAN'}
                    {num === 4 && 'PASAL 4: HAK KEKAYAAN INTELEKTUAL'}
                    {num === 5 && 'PASAL 5: SERVICE LEVEL AGREEMENT (SLA)'}
                    {num === 6 && 'PASAL 6: KERAHASIAAN'}
                    {num === 8 && 'PASAL 8: PENGAKHIRAN'}
                    {num === 9 && 'PASAL 9: PENYELESAIAN SENGKATA'}
                    {num === 10 && 'PASAL 10: HUKUM YANG BERLAKU'}
                  </h3>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {num === 3 && 'Content for Payment Terms clause...'}
                    {num === 5 && 'Content for SLA clause...'}
                    {/* Add full content for each clause */}
                  </p>
                </div>
              ))}

              {/* Signature Section */}
              <div className="mt-12 pt-8 border-t-2 border-gray-300">
                <p className="text-sm text-gray-700 mb-8">
                  Demikian Perjanjian ini dibuat dalam 2 (dua) rangkap asli, masing-masing mempunyai kekuatan hukum yang sama, dan ditandatangani oleh para pihak pada tanggal tersebut di atas.
                </p>
                <div className="grid grid-cols-2 gap-12">
                  <div>
                    <p className="text-sm font-semibold text-gray-900 mb-16">PIHAK PERTAMA</p>
                    <div className="border-t border-gray-400 pt-2">
                      <p className="text-sm font-semibold text-gray-900">PT DEF 2026</p>
                      <p className="text-sm text-gray-600">Direktur Utama</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 mb-16">PIHAK KEDUA</p>
                    <div className="border-t border-gray-400 pt-2">
                      <p className="text-sm font-semibold text-gray-900">PT Service Provider Indonesia</p>
                      <p className="text-sm text-gray-600">Direktur Utama</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* AI Compare Modal */}
      {showCompareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowCompareModal(false)}>
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <i className="fa-solid fa-wand-magic-sparkles text-purple-600 mr-3"></i>
                AI Document Comparison Results
              </h2>
              <button onClick={() => setShowCompareModal(false)} className="text-gray-500 hover:text-gray-700">
                <i className="fa-solid fa-xmark text-2xl"></i>
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <div className="flex items-start space-x-3">
                  <i className="fa-solid fa-circle-info text-purple-600 text-xl mt-1"></i>
                  <div>
                    <p className="text-sm font-medium text-purple-900 mb-1">Analysis Complete</p>
                    <p className="text-sm text-purple-700">AI has compared your draft with 3 supporting documents and identified 12 discrepancies and suggestions.</p>
                  </div>
                </div>
              </div>
              {/* Add comparison results here */}
            </div>
            <div className="border-t border-gray-200 p-6 flex items-center justify-between bg-gray-50">
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <i className="fa-solid fa-clock"></i>
                <span>Analysis completed in 2.3 seconds</span>
              </div>
              <div className="flex items-center space-x-2">
                <button onClick={() => setShowCompareModal(false)} className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">Close</button>
                <button className="px-5 py-2.5 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700">Export Report</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DraftingWorkspace;
